<template>
  <div class="comp-fmg-search">
    <!-- Search bar -->
    <div class="search-row">
      <div class="search-left">
        <searchbox v-model="search" />
      </div>

      <div class="search-right">
        <sprbutton
          type="primary"
          :size="4"
          :title="$t('userAdmin.addNewGroup')"
          icon="plus"
          click-event="create"
          @create="createNewFmg"
        />
      </div>
    </div>
    <CreateFmgModal
      :visible="showCreateModal"
      @create="handleCreateFmg"
      @cancel="showCreateModal = false"
    />
    <!-- Header row -->
    <div class="row sorter" v-if="filteredGroups.length">
      <div class="col-12">
        <div class="header-row">
          <!-- NAME -->
          <div class="header-name" @click="sortBy('name')">
            {{ $t('userAdmin.sortByName') }}
            <font-awesome-icon
              :icon="getSortIcon('name')"
              :class="[getSortClass('name'), 'sortable-icon']"
            />
          </div>

          <!-- RISKY -->
          <div class="header-risky" @click="sortBy('riskyFriendshipsCount')">
            {{ $t('userAdmin.riskyFriendships') }}
            <font-awesome-icon
              :icon="getSortIcon('riskyFriendshipsCount')"
              :class="[getSortClass('riskyFriendshipsCount'), 'sortable-icon']"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- FMG ITEMS -->
    <FriendManagementGroupListItem
      v-for="g in filteredGroups"
      :key="g.id"
      :group="g"
      :pool-sections="poolSections"
      @calculate-impact="calculateImpact"
      @save-sections="saveSections"
      @delete-fmg="handleDeleteFmg"
    />

    <!-- Empty -->
    <div
      v-if="!loading && filteredGroups.length === 0"
      class="text-center my-4"
    >
      {{ $t('common.noResults') }}
    </div>

    <!-- Loading -->
    <div v-if="loading" class="text-center my-4">
      <loading />
    </div>
  </div>
</template>

<script lang="ts">
import Vue from 'vue'
import searchbox from '../SPRSearchBox.vue'
import loading from '../SPRLoading.vue'
import FriendManagementGroupListItem from './FriendManagementGroupSearchResult.vue'
import sprbutton from '../SPRButton.vue'
import CreateFmgModal from './CreateFmgModal.vue'
import {
  FriendManagementDto,
  SectionSimpleDto,
  SearchResult,
  RiskyFriendshipDto
} from '@/types'

export default Vue.extend({
  name: 'FriendManagementGroupManagement',

  components: {
    searchbox,
    loading,
    FriendManagementGroupListItem,
    sprbutton,
    CreateFmgModal
  },

  data() {
    return {
      groups: [] as FriendManagementDto[],
      poolSections: [] as SectionSimpleDto[],
      search: '',
      loading: false,
      sortedColumn: 'name',
      sortDirection: 'asc' as 'asc' | 'desc',
      showCreateModal: false
    }
  },

  computed: {
    filteredGroups(): FriendManagementDto[] {
      let list = [...this.groups]

      if (this.search) {
        const s = this.search.toLowerCase()
        list = list.filter((g) => g.name.toLowerCase().includes(s))
      }

      list.sort((a, b) => {
        const col = this.sortedColumn as keyof FriendManagementDto
        const av = a[col] ?? ''
        const bv = b[col] ?? ''
        if (av < bv) return this.sortDirection === 'asc' ? -1 : 1
        if (av > bv) return this.sortDirection === 'asc' ? 1 : -1
        return 0
      })

      return list
    }
  },

  mounted() {
    this.loadEverything()
  },

  methods: {
    async loadEverything() {
      this.loading = true
      await Promise.all([this.loadGroups(), this.loadSectionPoolSections()])
      this.loading = false
    },

    async loadGroups() {
      const url =
        this.$store.state.apiBases.friendservice +
        this.$store.state.apiEndpoints.friendManagementGroupsSearchResult[0]

      try {
        const response = await this.$http.get(url)
        const result = response.data as SearchResult<FriendManagementDto>
        this.groups = result.results || []
      } catch (err) {
        console.error(err)
        // @ts-ignore
        this.showAlertError(this.$t('common.fetchFailed'))
      }
    },

    async loadSectionPoolSections() {
      const url =
        this.$store.state.apiBases.friendservice +
        this.$store.state.apiEndpoints.sectionPoolSections[0]

      try {
        const response = await this.$http.get(url)
        this.poolSections = response.data.results || []
      } catch (err) {
        console.warn('Could not load pool sections (maybe API not ready):', err)
        this.poolSections = []
      }
    },

    // Sorting helpers
    sortBy(column: string) {
      if (this.sortedColumn === column) {
        this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc'
      } else {
        this.sortedColumn = column
        this.sortDirection = 'asc'
      }
    },

    getSortIcon(column: string) {
      if (this.sortedColumn !== column) return 'sort'
      return this.sortDirection === 'asc' ? 'sort-down' : 'sort-up'
    },

    getSortClass(column: string) {
      return this.sortedColumn === column ? 'brand-red' : 'brand-grey16'
    },

    // ============================
    // CHILD → PARENT EVENTS
    // ============================
    async calculateImpact(
      groupId: number,
      removeIds: number[],
      addIds: number[],
      done: (updated: RiskyFriendshipDto[]) => void
    ) {
      if (removeIds.length === 0) {
        done([])
        return
      }
      const url =
        this.$store.state.apiBases.friendservice +
        this.$store.state.apiEndpoints.fmgCalculateImpact[0]

      const payload = {
        friendManagementGroupId: groupId,
        removeSectionIds: removeIds
      }

      try {
        const res = await this.$http.post(url, payload)
        const list = (res.data || []) as RiskyFriendshipDto[]
        done(list)
      } catch (err) {
        console.error('Failed to calculate impact', err)
        done([])

        // @ts-ignore
        this.showAlertError(this.$t('common.fetchFailed'))
      }
    },
    createNewFmg() {
      this.showCreateModal = true
    },
    async handleCreateFmg(name: string) {
      this.showCreateModal = false

      const url =
        this.$store.state.apiBases.friendservice +
        this.$store.state.apiEndpoints.fmgCreate[0]

      try {
        const payload = { name }
        await this.$http.post(url, payload)

        // @ts-ignore
        this.showAlertSuccess(this.$t('userAdmin.fmgCreatedSuccessfully'))

        await this.loadEverything()
      } catch (err: any) {
        console.error('Create FMG failed', err)
        const msg = err?.response?.data || this.$t('common.saveFailed')

        // @ts-ignore
        this.showAlertError(msg)
      }
    },
    async saveSections(groupId: number, removeIds: number[], addIds: number[]) {
      const base = this.$store.state.apiBases.friendservice

      const moveUrl = base + this.$store.state.apiEndpoints.fmgMoveToPool[0]

      const addUrl = base + this.$store.state.apiEndpoints.fmgAddFromPool[0]

      // No-op?
      if (
        (!removeIds || removeIds.length === 0) &&
        (!addIds || addIds.length === 0)
      ) {
        return
      }

      try {
        // ----------------------------
        // 1) REMOVE (FMG → Pool)
        // ----------------------------
        if (removeIds && removeIds.length > 0) {
          const payload = {
            friendManagementGroupId: groupId,
            removeSectionIds: removeIds
          }


          await this.$http.post(moveUrl, payload)

          // @ts-ignore
          this.showAlertSuccess(this.$t('userAdmin.sectionsMovedToPool'))
        }

        // ----------------------------
        // 2) ADD (Pool → FMG)
        // ----------------------------
        if (addIds && addIds.length > 0) {
          const payload = {
            friendManagementGroupId: groupId,
            addSectionIds: addIds
          }


          await this.$http.post(addUrl, payload)

          // @ts-ignore
          this.showAlertSuccess(this.$t('userAdmin.sectionsAddedToFmg'))
        }

        // ----------------------------
        // 3) Refresh data after both ops
        // ----------------------------
        await this.loadEverything()
      } catch (err) {
        console.error('saveSections failure', err)

        let message = this.$t('common.saveFailed') as string

        if (err && typeof err === 'object' && 'response' in err) {
          // @ts-ignore
          message = err.response?.data || message
        }

        // @ts-ignore
        this.showAlertError(message)
      }
    },
    async handleDeleteFmg(groupId: number) {
      const confirmed = confirm(this.$t('userAdmin.confirmDeleteFmg') as string)
      if (!confirmed) return

      const url =
        this.$store.state.apiBases.friendservice +
        this.$store.state.apiEndpoints.fmgDelete[0].replace('{0}', groupId)

      try {
        await this.$http.delete(url)

        // @ts-ignore
        this.showAlertSuccess(this.$t('userAdmin.fmgDeletedSuccessfully'))

        await this.loadEverything()
      } catch (err: any) {
        console.error('Delete FMG failed', err)
        const msg = err?.response?.data || this.$t('common.saveFailed')

        // @ts-ignore
        this.showAlertError(msg)
      }
    }
  }
})
</script>

<style lang="scss">
@import '~@/assets/scss/_variables.scss';

.comp-fmg-search {
  .sorter {
    margin-top: 10px;
    background: $brand-white;
    border-top-left-radius: 5px;
    border-top-right-radius: 5px;

    .header-row {
      display: grid;
      grid-template-columns: 2fr 320px;
      height: 60px;
      align-items: center;
      padding: 0 10px;
      border-bottom: 1px solid $brand-grey7;
    }

    .header-name,
    .header-risky {
      font-family: 'Signa-Light', Sans-Serif;
      font-size: 14px;
      line-height: 60px;
      color: $brand-grey16;
      display: flex;
      align-items: center;
      gap: 6px;
      cursor: pointer;
      user-select: none;
    }

    .header-risky {
      justify-content: flex-end;
      text-align: right;
    }

    .sortable-icon {
      color: $brand-red;
    }
  }
  .search-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;
  }

  .search-left {
    flex: 0 0 50%;
    max-width: 400px;
  }

  .search-right {
    flex: 0 0 auto;
  }
}
</style>
